import { AutogenLayoutOrderPayablesGroupByTable } from "./autogen/AutogenLayoutOrderPayablesGroupByTable";
import { Currency, CurrencySettings, DisplayType, DisplayValue, HorizontalAlignment, ModelRow } from "@mcleod/core";
import {
    Button, ChangeEvent, DropdownItem, Image, Label, Layout, ModelDataChangeType, PrintableEvent, Tab, Table, TableCell,
    TableColumn, TableRow, TableRowDisplayEvent, TableSelectionEvent, Tabset, Textbox
} from "@mcleod/components";
import { CommonTooltips } from "@mcleod/common";
import { OrderPayables, OrderPayablesTabLayout, PayablesLookup } from "./OrderPayables";
import { OrderPayablesFilter } from "./OrderPayablesFilter";
import { OrderPayablesHold } from "./OrderPayablesHold";
import { OrderPayablesUtil } from "./OrderPayablesUtil";
import { OrderPayablesProcess } from "./OrderPayablesProcess";
import { OrderPayablesPendingReview } from "./OrderPayablesPendingReview";

export const GroupByItems = [
    { value: "O", displayValue: "OK to Pay Date", fieldName: "ok2pay_date" },
    { value: "P", displayValue: "Payee", fieldName: "payee_id" },
    { value: "F", displayValue: "Factoring Company", fieldName: "factoring_company_id" }
];

export class OrderPayablesGroupByTable extends AutogenLayoutOrderPayablesGroupByTable {
    private tabsetOrderPayables: Tabset;
    private profileFilter: OrderPayablesFilter;
    public lookupData: PayablesLookup;

    override onLoad(): void {
        this.textboxGroupBy.items = GroupByItems.map(item => new DropdownItem(item.value, item.displayValue));
    }

    initialize(tabset: Tabset, groupByDefault: string, profileFilter: OrderPayablesFilter, lookupData: PayablesLookup): void {
        this.tabsetOrderPayables = tabset;
        this.profileFilter = profileFilter;
        this.lookupData = lookupData;
        this.setCustomFilter();
        if (this.textboxGroupBy.selectedItem?.value != groupByDefault) {
            this.textboxGroupBy.selectedItem = (this.textboxGroupBy.items as DropdownItem[]).find(item => item.value == groupByDefault);
        } else {
            this.groupByField();
        }
    }

    async setTotal() {
        const total = this.createFilteredCurrency();
        if (this.tableGroupBy.selectedRows.length == 0) {
            for (const row of this.tableGroupBy.rows) {
                await this.addCurrency(total, row.data.getCurrency("process_total", 0));
                await this.addCurrency(total, row.data.getCurrency("hold_total", 0));
            }
            this.textboxTotal.caption = "Total";
        } else {
            for (const row of this.tableGroupBy.selectedRows) {
                await this.addCurrency(total, row.data.getCurrency("process_total", 0));
                await this.addCurrency(total, row.data.getCurrency("hold_total", 0));
            }
            this.textboxTotal.caption = "Selected Total";
        }
        this.textboxTotal.text = total.toString();
        this.setTabTitles();
    }

    async groupByField() {
        if (this.tableGroupBy.selectedRows.length > 0) {
            this.tableGroupBy.selectedRows = [];
        }
        const groupByMap: Map<string, {
            count: number;
            hold_total: Currency;
            process_total: Currency;
            freeze_by: string;
            name: string;
            hasPayablesInCurrency: boolean;
        }> = new Map();

        for (const row of this.sourceOrderPayablesCache.data) {
            const groupByValue = row.get(this.getGroupByFieldName());
            if (!groupByMap.has(groupByValue)) {
                groupByMap.set(groupByValue, {
                    count: 0,
                    hold_total: this.createFilteredCurrency(),
                    process_total: this.createFilteredCurrency(),
                    freeze_by: row.get("freeze_by", null),
                    name: this.getGroupByFieldName() === "payee_id" ? row.get("payee_name") : this.getGroupByFieldName() === "factoring_company_id" ? row.get("factoring_company_name") : null,
                    hasPayablesInCurrency: false
                });
            }
            const multiCurrencyEnabled = CurrencySettings.getSingleton().multiCurrencyEnabled();
            const totalPay = row.getCurrency("pay_amount");
            if (multiCurrencyEnabled) {
                const currencyCode = totalPay?.getCurrencyCode();
                if (currencyCode != null && currencyCode === this.profileFilter.getFilterCurrencyType()) {
                    groupByMap.get(groupByValue).hasPayablesInCurrency = true;
                }
            }
            else {
                groupByMap.get(groupByValue).hasPayablesInCurrency = true;
            }
            groupByMap.get(groupByValue).count++;
            if (row.get("ready_to_pay_flag") == "Y") {
                await this.addCurrency(groupByMap.get(groupByValue).process_total, totalPay);
            } else {
                await this.addCurrency(groupByMap.get(groupByValue).hold_total, totalPay);
            }
        }

        const rows = [];
        groupByMap.forEach((value, key) => {
            if (!value.hasPayablesInCurrency) return;
            const row = new ModelRow("", false);
            row.set("group_by_value", key);
            row.set("count", value.count);
            row.set("hold_total", value.hold_total);
            row.set("process_total", value.process_total);
            row.set("freeze_by", value.freeze_by);
            row.set("name", value.name);
            rows.push(row);
        });
        this.tableGroupBy.displayComponentData(null, rows, 0);
        await this.setTotal();

        // set table sort based on group by
        const column: TableColumn = this.tableGroupBy.columns.find(column => column.headingCell.id == "columnHeaderGroupBy");
        if (column == null) return;
        const tabMap = new Map<string, Table>;
        this.tabsetOrderPayables.forEveryChildComponent((component) => {
            if (component instanceof Layout && OrderPayables.isOrderPayablesLayoutTab(component)) {
                const table = (component as OrderPayablesTabLayout).tableOrderPayables;
                tabMap.set(component.id, table);
            }
        });
        const frozenPayableColumn = this.tableGroupBy.columns.find(column => column.headingCell.id == "columnHeaderFrozenPayable");
        switch (this.textboxGroupBy.selectedItem.value) {
            case "O": {
                column.headingCell.findComponentByType(Label).text = "OK to Pay Date";
                column.headingCell.findComponentByType(Label).tooltip = "OK to Pay Date";
                this.tableGroupBy.applySort("group_by_value", "asc");
                this.tableGroupBy.setColumnVisibility(frozenPayableColumn, false);
                break;
            }
            case "P": {
                column.headingCell.findComponentByType(Label).text = "Payee";
                column.headingCell.findComponentByType(Label).tooltip = "Payee";
                this.tableGroupBy.applySort("name", "asc");
                this.tableGroupBy.setColumnVisibility(frozenPayableColumn, true);
                break;
            }
            case "F": {
                column.headingCell.findComponentByType(Label).text = "Factoring Company";
                column.headingCell.findComponentByType(Label).tooltip = "Factoring Company";
                this.tableGroupBy.applySort("name", "asc");
                this.tableGroupBy.setColumnVisibility(frozenPayableColumn, true);
                break;
            }
        }
        tabMap.forEach((table, key) => {
            table.resetColumnSorting().then(() => {
                switch (this.textboxGroupBy.selectedItem.value) {
                    case "O": {
                        table.applySort("ok2pay_date", "asc");
                        table.applySort("payee_name", "asc", true);
                        table.applySort("order_id", "asc", true);
                        table.applySort("record_type", "desc", true);
                        break;
                    }
                    case "P": {
                        table.applySort("payee_name", "asc");
                        table.applySort("factoring_company_name", "asc", true);
                        table.applySort("ok2pay_date", "asc", true);
                        table.applySort("order_id", "asc", true);
                        table.applySort("record_type", "desc", true);
                        break;
                    }
                    case "F": {
                        table.applySort("factoring_company_name", "asc");
                        table.applySort("payee_name", "asc", true);
                        table.applySort("ok2pay_date", "asc", true);
                        table.applySort("order_id", "asc", true);
                        table.applySort("record_type", "desc", true);
                    }
                }
            });
        });
    }

    public getGroupByFieldName() {
        return GroupByItems.find(item => item.value == this.textboxGroupBy.selectedItem.value).fieldName;
    }

    setCustomFilter() {
        this.tabsetOrderPayables.forEveryChildComponent((component) => {
            if (component instanceof Layout && OrderPayables.isOrderPayablesLayoutTab(component)) {
                const layout: OrderPayablesTabLayout = component as OrderPayablesTabLayout;
                layout.filterPanel.applyRemoveFilter = (filter) => {
                    layout.tableOrderPayables.customFilter = this.getGroupByCustomFilter(layout);
                };

                // Set custom filter update method based on layout type
                const fullDataSource = this.sourceOrderPayablesCache.data;

                if (layout instanceof OrderPayablesPendingReview) {
                    // Special filter count handling for PendingReview layout
                    layout.filterPanel.updateFilterCounts = () => {
                        const filters = {
                            exception: {
                                filter: layout.filterPanel.filters.find(f => f.id === "exception"),
                                getCount: (row: ModelRow) => !row.isNull("submittal_exception") && layout.getLayoutBaseFilter(row)
                            },
                            readyToSubmit: {
                                filter: layout.filterPanel.filters.find(f => f.id === "readyToSubmit"),
                                getCount: (row: ModelRow) => row.get("ready_to_submit") &&
                                    row.isNull("freeze_by") &&
                                    layout.getLayoutBaseFilter(row)
                            },
                            submittedToTriumphPay: {
                                filter: layout.filterPanel.filters.find(f => f.id === "submittedToTriumphPay"),
                                getCount: (row: ModelRow) => row.get("triumphpay_submitted") && layout.getLayoutBaseFilter(row)
                            }
                        };

                        Object.entries(filters).forEach(([_, config]) => {
                            if (config.filter) {
                                const count = fullDataSource.filter(config.getCount).length;
                                config.filter.count = count;
                                config.filter.button.caption = `${config.filter.caption} ${count}`;
                            }
                        });
                    };
                } else {
                    // Default filter count handling for other layouts
                    layout.filterPanel.updateFilterCounts = async () => {
                        // Existing implementation remains unchanged
                        if (layout instanceof OrderPayablesHold) {
                            layout.refreshDynamicTaskFilters();
                        }

                        if (layout.filterPanel.filters.length == 0) return;

                        // reset counts
                        for (const filter of layout.filterPanel.filters) {
                            filter.count = 0;
                            filter.button.caption = `${filter.caption} ${filter.count}`;
                        }
                        const tableOrderPayables = layout.tableOrderPayables;
                        const currencySettings = CurrencySettings.getSingleton();
                        for (const tableRow of tableOrderPayables.allRows) {
                            const modelRow = tableRow.data as ModelRow;
                            const selectedRows = this.tableGroupBy.selectedRows;
                            const groupByMatches = selectedRows.length == 0 || selectedRows.find((tableRow: TableRow) => tableRow.data.get("group_by_value") == modelRow.get(this.getGroupByFieldName())) != null;
                            for (const filter of layout.filterPanel.filters) {
                                if (filter.filter(modelRow) && layout.getLayoutBaseFilter(modelRow) && !this.shouldRollUp(modelRow) && groupByMatches) {
                                    if (!currencySettings.multiCurrencyEnabled() || modelRow.getCurrency("pay_amount").getCurrencyCode() == this.profileFilter.getFilterCurrencyType()) {
                                        filter.count++;
                                        filter.button.caption = `${filter.caption} ${filter.count}`;
                                    }
                                }
                            }
                        }

                        if (layout instanceof OrderPayablesHold) this.updateHoldTabFilterVisibility(layout);
                    };
                }

                // Set custom filter for all layouts
                layout.tableOrderPayables.customFilter = this.getGroupByCustomFilter(layout);

                // Update filter counts
                layout.filterPanel.updateFilterCounts();
            }
        });
    }

    updateHoldTabFilterVisibility(layout: OrderPayablesTabLayout) {
        const top10Reasons = layout.filterPanel.filters.filter(f => f.dynamic).sort((a, b) => b.count - a.count).slice(0, 10);
        layout.filterPanel.filters.filter(f => f.dynamic).forEach(f => {
            f.button.visible = top10Reasons.includes(f) && f.count > 0;
        });
        const activeFilter = layout.filterPanel.getActiveFilter();
        if (activeFilter != null && !activeFilter.button.visible) {
            layout.filterPanel.clearActiveFilter();
            layout.filterPanel.removeFilterFromPanel(activeFilter);
        }
        const sortedButtons = layout.filterPanel.panel.components.sort((a, b) => {
            if (a instanceof Button && b instanceof Button) {
                return a.caption.localeCompare(b.caption);
            }
            return 0;
        });
        sortedButtons.push(new Button()); //TODO: Temporary fix for last button getting removed when setting components below
        layout.filterPanel.panel.components = sortedButtons;
    }

    shouldRollUp(row: ModelRow): boolean {
        let shouldRollUp: boolean = false;
        if (row.get("record_type") == "P") {
            const settlementRow = OrderPayablesUtil.settlementInTabWithAdjustment(row, this.lookupData);
            if (settlementRow != null) {
                const selectedRows = this.tableGroupBy.selectedRows;
                shouldRollUp = selectedRows.length == 0 || selectedRows.some((tableRow: TableRow) => tableRow.data.get("group_by_value") == settlementRow.get(this.getGroupByFieldName()));
            }
        }
        return shouldRollUp;
    }

    groupByHasPayablesInCurrency(groupByRow: ModelRow): boolean {
        const currencySettings = CurrencySettings.getSingleton();
        const multicurrencyEnabled = currencySettings.multiCurrencyEnabled();
        if (!multicurrencyEnabled) return true;
        // get records for group by row
        const groupByValue = groupByRow.get(this.getGroupByFieldName());
        const groupByData = this.sourceOrderPayablesCache.data.filter(row => row.get(this.getGroupByFieldName()) == groupByValue);
        // check if any of the records have payables in the current currency
        return groupByData.some(row => this.profileFilter.currencyMatches(row));
    }

    hasPayablesInCurrency(row: ModelRow): boolean {
        const currencySettings = CurrencySettings.getSingleton();
        const multicurrencyEnabled = currencySettings.multiCurrencyEnabled();
        if (!multicurrencyEnabled) return true;
        let hasPayablesInCurrency: boolean = false;
        if (this.profileFilter.currencyMatches(row)) return true;
        const payableDataByPayee = this.lookupData.getPayablesByPayee(row.get("payee_id"));
        for (const payableData of payableDataByPayee.getSettlements().values()) {
            if (this.profileFilter.dataPasses(payableData) && this.profileFilter.currencyMatches(payableData)) {
                hasPayablesInCurrency = true;
                break;
            }
        }

        for (const payableData of payableDataByPayee.getAdjustments().values()) {
            if (this.profileFilter.dataPasses(payableData) && this.profileFilter.currencyMatches(payableData)) {
                hasPayablesInCurrency = true;
                break;
            }
        }
        return hasPayablesInCurrency;
    }

    refreshCustomFilter() {
        this.tabsetOrderPayables.forEveryChildComponent((component) => {
            if (component instanceof Layout && OrderPayables.isOrderPayablesLayoutTab(component)) {
                const layout: OrderPayablesTabLayout = component as OrderPayablesTabLayout;
                layout.tableOrderPayables.customFilter = this.getGroupByCustomFilter(layout);
                layout.filterPanel.updateFilterCounts();
            }
        });
    }

    tableGroupByOnSelect(event: TableSelectionEvent) {
        this.refreshCustomFilter();
        this.updateRowsSelectedButton();
        this.buttonClearSelections.enabled = this.tableGroupBy.selectedRows.length > 0;
        this.selectFirstNonZeroTab();
        this.setTotal();
        this.setSelectedRowsInProcessTab();

    }

    private setSelectedRowsInProcessTab() {
        const selectedRows = this.tableGroupBy.selectedRows;
        const processTab = this.tabsetOrderPayables.findComponentById("layoutOrderPayablesProcess") as OrderPayablesProcess;
        processTab.selectedGroupByRows = selectedRows;
        processTab.selectedGroupByValue = this.getGroupByFieldName();
    }

    selectFirstNonZeroTab() {
        const currentTab = this.tabsetOrderPayables.getTab(this.tabsetOrderPayables.selectedIndex);
        const tabMap = new Map<string, Table>;
        this.tabsetOrderPayables.forEveryChildComponent((component) => {
            if (component instanceof Layout && OrderPayables.isOrderPayablesLayoutTab(component)) {
                const layout = component as OrderPayablesTabLayout;
                const table = layout.tableOrderPayables;
                tabMap.set(component.parent.id, table);
            }
        });
        if (tabMap.get(currentTab.id).filteredRows.length > 0) {
            return;
        }
        this.tabsetOrderPayables.tabs.some((tab, index) => {
            if (tab.id != currentTab.id && tabMap.get(tab.id).filteredRows.length > 0) {
                tab.select();
                return true;
            }
        });
    }

    updateRowsSelectedButton() {
        this.buttonClearSelections.caption = `Clear ${this.tableGroupBy.selectedRows.length.toString()} ${this.tableGroupBy.selectedRows.length == 1 ? "Selection" : "Selections"}`;
        this.buttonClearSelections.tooltip = this.generateRowsSelectedTooltip();
    }

    tableGroupByOnRowDisplay(event: TableRowDisplayEvent) {
        const tableRow = event.getTableRow();
        const modelRow = tableRow.data;
        const labelGroupByValue = tableRow.findComponentById("labelGroupByValue") as Label;
        const labelName = tableRow.findComponentById("labelName") as Label;
        labelGroupByValue.visible = this.textboxGroupBy.selectedItem.value == "O";
        labelName.visible = !labelGroupByValue.visible;
        if (this.textboxGroupBy.selectedItem.value == "O") {
            const weekday = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
            const dateDisplayValue = DisplayValue.getDisplayValue(modelRow.get("group_by_value"), DisplayType.DATE);
            const d = new Date(dateDisplayValue);
            labelGroupByValue.text = dateDisplayValue + " " + weekday[d.getDay()];
        } else if (this.textboxGroupBy.selectedItem.value == "P") {
            CommonTooltips.setTooltipContentLayout(labelName, null, "lme/settlement/SettlementPayeeQuickInfo", null, { id: modelRow.get("group_by_value") });
        } else if (this.textboxGroupBy.selectedItem.value == "F") {
            CommonTooltips.setTooltipContentLayout(labelName, null, "lme/settlement/FactoringCompanyQuickInfo", null, { id: modelRow.get("group_by_value") });
        }
        const imageFrozenPayable = tableRow.findComponentById("imageFrozenPayable") as Image;
        if (imageFrozenPayable != null) {
            imageFrozenPayable.visible = !modelRow.isNull("freeze_by");
        }
    }


    getGroupByCustomFilter(layout: OrderPayablesTabLayout): (row: TableRow) => boolean {
        const activeTaskFilter = layout.filterPanel.getActiveFilter();
        const baseLayoutFilter = layout.getLayoutBaseFilter;

        // Handle OrderPayablesPendingReview visibility state properly
        if (layout instanceof OrderPayablesPendingReview) {
            // Create a function to update column visibility that we can call both initially and during filtering
            const updateColumnVisibility = () => {
                // Get the current filter status to determine visibility state
                const activeFilter = layout.filterPanel.getActiveFilter();
                let visibilityState;

                if (activeFilter) {
                    const filterConfig = layout.filterConfigs?.[activeFilter.id];
                    if (filterConfig) {
                        visibilityState = filterConfig.getVisibility(true);
                    } else {
                        visibilityState = layout.NO_FILTER_VISIBILITY;
                    }
                } else {
                    visibilityState = layout.NO_FILTER_VISIBILITY;
                }

                // Always ensure exception column is only visible when exception filter is active
                const exceptionFilterActive = layout.filterPanel.filters.find(f => f.id === "exception")?.active || false;
                visibilityState.showException = exceptionFilterActive;

                // Apply visibility update
                layout.columnManager.updateVisibility(visibilityState);
            };

            // Call it immediately to set the initial state
            updateColumnVisibility();

            // Return filter function with column visibility management
            return (row: TableRow) => {
                const taskFilterMatches = activeTaskFilter == null || activeTaskFilter.filter(row.data);
                const shouldDisplay = this.shouldDisplayRow(row.data);

                // Update column visibility after filtering
                if (taskFilterMatches && baseLayoutFilter(row.data) && shouldDisplay) {
                    // Use setTimeout to ensure this runs after filtering is complete
                    setTimeout(updateColumnVisibility, 0);
                }

                return taskFilterMatches && baseLayoutFilter(row.data) && shouldDisplay;
            };
        }

        // Handle Process layout separately
        if (layout instanceof OrderPayablesProcess) {
            return (row: TableRow) => {
                const taskFilterMatches: boolean = activeTaskFilter == null || activeTaskFilter.filter(row.data);
                const shouldDisplay = this.shouldDisplayRow(row.data);
                const hasPayablesInCurrency = this.hasPayablesInCurrency(row.data);

                if (shouldDisplay) {
                    OrderPayablesUtil.setPayAdjustmentInfo(row, this, this.lookupData);
                    if (CurrencySettings.getSingleton().multiCurrencyEnabled() && row.data.get("record_type") == "P") {
                        row.enabled = this.profileFilter.getFilterCurrencyType() == row.data.getCurrency("pay_amount")?.getCurrencyCode();
                    }
                }

                if (taskFilterMatches && baseLayoutFilter(row.data) && shouldDisplay) {
                    layout.updateColumnVisibility();
                }

                return taskFilterMatches && baseLayoutFilter(row.data) && shouldDisplay && hasPayablesInCurrency;
            };
        }

        // Default filter behavior for other layouts
        return (row: TableRow) => {
            const taskFilterMatches: boolean = activeTaskFilter == null || activeTaskFilter.filter(row.data);
            const isExceptionTaskFilter = activeTaskFilter != null && activeTaskFilter.id == "exception";
            const isSubmittedTaskFilter = activeTaskFilter != null && activeTaskFilter.id == "submittedToTriumphPay";
            const shouldDisplay = this.shouldDisplayRow(row.data);

            if (shouldDisplay) {
                OrderPayablesUtil.setPayAdjustmentInfo(row, this, this.lookupData);
                if (CurrencySettings.getSingleton().multiCurrencyEnabled() && row.data.get("record_type") == "P") {
                    row.enabled = this.profileFilter.getFilterCurrencyType() == row.data.getCurrency("pay_amount")?.getCurrencyCode();
                }

                // If this is a Hold layout, handle column visibility based on filter type
                if (layout instanceof OrderPayablesHold) {
                    const isStaticFilter = activeTaskFilter != null && !activeTaskFilter.dynamic;

                    // Handle Type column for dynamic filters
                    const typeCell = row.findComponentById("tableOrderPayablesTypeColumn") as TableCell;
                    if (typeCell && activeTaskFilter?.dynamic === true) {
                        typeCell.visible = true;
                        const typeImage = row.findComponentById("imageRecordType") as Image;
                        if (typeImage) {
                            typeImage.visible = true;
                            typeImage.marginTop = "0";
                            typeImage.marginBottom = "0";
                        }
                    }

                    // Handle Exception, Payment Status, and Payment ID visibility
                    const exceptionCell = row.findComponentById("tableOrderPayablesExceptionColumn") as TableCell;
                    if (exceptionCell) {
                        exceptionCell.visible = isStaticFilter && isExceptionTaskFilter;
                        if (exceptionCell.visible) {
                            const labelException = row.findComponentById("labelException") as Label;
                            if (labelException) {
                                labelException.visible = true;
                            }
                        }
                    }

                    // Additional cells visibility handling...
                    const paymentStatusCell = row.findComponentById("paymentStatusCell") as TableCell;
                    if (paymentStatusCell) {
                        paymentStatusCell.visible = isStaticFilter && isSubmittedTaskFilter;
                        // Cell contents visibility handling...
                    }

                    const payAdjustmentWarningCell = row.findComponentById("adjustmentWarningCell") as TableCell;
                    if (payAdjustmentWarningCell) {
                        payAdjustmentWarningCell.visible = !isStaticFilter || (isStaticFilter && !isSubmittedTaskFilter);
                        // Warning image visibility handling...
                    }

                    const paymentIdCell = row.findComponentById("paymentIdCell") as TableCell;
                    if (paymentIdCell) {
                        paymentIdCell.visible = isStaticFilter && isSubmittedTaskFilter;
                        // Cell contents visibility handling...
                    }
                }
            }

            return taskFilterMatches && baseLayoutFilter(row.data) && shouldDisplay;
        };
    }

    shouldDisplayRow(row: ModelRow): boolean {
        const selectedRows = this.tableGroupBy.selectedRows;
        const groupByMatches: boolean = selectedRows.length == 0 || selectedRows.some((tableRow: TableRow) => tableRow.data.get("group_by_value") == row.get(this.getGroupByFieldName()));
        return this.profileFilter.dataPasses(row) && !this.shouldRollUp(row) && groupByMatches;
    }

    buttonClearSelectionsOnClick(): void {
        this.tableGroupBy.selectedRows = [];
        this.refreshCustomFilter();
    }

    generateRowsSelectedTooltip(): string {
        const groupByValues = this.tableGroupBy.selectedRows.map(row => row.data.get("group_by_value"));
        if (groupByValues.length == 0) {
            return null;
        }
        groupByValues.sort();
        if (this.textboxGroupBy.selectedItem.value == "O") {
            let rangeStart = groupByValues[0];
            let rangeEnd = groupByValues[0];
            const ranges = [];
            const MILLISECONDS_IN_A_DAY = 86400000;
            for (let i = 1; i < groupByValues.length; i++) {
                const diff = new Date(groupByValues[i]).getTime() - new Date(rangeEnd).getTime();
                if (diff == MILLISECONDS_IN_A_DAY) {
                    rangeEnd = groupByValues[i];
                } else {
                    ranges.push({ start: rangeStart, end: rangeEnd });
                    rangeStart = groupByValues[i];
                    rangeEnd = groupByValues[i];
                }
            }
            ranges.push({ start: rangeStart, end: rangeEnd });
            return ranges.map(range => {
                const sameDay = new Date(range.start).getDate() == new Date(range.end).getDate();
                if (sameDay) {
                    return "• " + DisplayValue.getDisplayValue(range.start, DisplayType.DATE);
                }
                return "• " + DisplayValue.getDisplayValue(range.start, DisplayType.DATE) + " - " + DisplayValue.getDisplayValue(range.end, DisplayType.DATE);
            }).join("\n");
        } else {
            return groupByValues.map(value => "• " + value).join("\n");
        }
    }

    async updateGroupByTable(oldData: ModelRow, newData: ModelRow, changeType: ModelDataChangeType) {
        if (newData == null || (oldData == null && changeType == ModelDataChangeType.DELETE)) return;
        const existingRow = this.tableGroupBy.filteredRows.find(row => row.data.get("group_by_value") == oldData?.get(this.getGroupByFieldName()));
        if (changeType == ModelDataChangeType.DELETE) {
            await this.handleDelete(oldData, existingRow);
        } else if (changeType == ModelDataChangeType.UPDATE && oldData == null) {
            await this.handleAdd(newData);
        } else if (changeType == ModelDataChangeType.UPDATE) {
            await this.handleUpdate(oldData, newData, existingRow);
        }
        this.updateRowsSelectedButton();
        await this.setTotal();
    }

    async handleDelete(oldData: ModelRow, existingRow: TableRow) {
        existingRow.data.set("count", existingRow.data.get("count") - 1);
        const groupByHasPayablesInCurrency = this.groupByHasPayablesInCurrency(existingRow.data);
        if (existingRow.data.get("count") == 0 || !groupByHasPayablesInCurrency) {
            this.tableGroupBy.removeRow(existingRow.index);
            if (this.tableGroupBy.selectedRows.length === 0) {
                this.sourceOrderPayablesCache.displayDataInBoundComponents();
            }
        } else if (oldData.getString("ready_to_pay_flag") == "Y") {
            await this.subCurrency(existingRow.data.get("process_total"), oldData.getCurrency("pay_amount"));
            this.tableGroupBy.redisplaySingleRow(existingRow.index, existingRow.data, false, false);
        } else if (oldData.getString("ready_to_pay_flag") == "N") {
            await this.subCurrency(existingRow.data.get("hold_total"), oldData.getCurrency("pay_amount"));
            this.tableGroupBy.redisplaySingleRow(existingRow.index, existingRow.data, false, false);
        }
    }

    async handleAdd(newData: ModelRow) {
        const findRow = this.tableGroupBy.allRows.find(row => row.data.get("group_by_value") == newData.get(this.getGroupByFieldName()) && row.data.get("count") > 0);
        if (findRow == null) {
            this.createNewGroupByRow(newData);
        } else {
            findRow.data.set("count", findRow.data.get("count") + 1);
            if (newData.getString("ready_to_pay_flag") == "Y") {
                await this.addCurrency(findRow.data.get("process_total"), newData.getCurrency("pay_amount"));
            } else {
                await this.addCurrency(findRow.data.get("hold_total"), newData.getCurrency("pay_amount"));
            }
            this.tableGroupBy.redisplaySingleRow(findRow.index, findRow.data, false, false);
        }
    }

    async handleUpdate(oldData: ModelRow, newData: ModelRow, existingRow: TableRow) {
        if (oldData.get(this.getGroupByFieldName()) == newData.get(this.getGroupByFieldName())) {
            if (oldData.getString("ready_to_pay_flag") == "Y" && newData.getString("ready_to_pay_flag") == "Y") {
                await this.subCurrency(existingRow.data.get("process_total"), oldData.getCurrency("pay_amount"));
                await this.addCurrency(existingRow.data.get("process_total"), newData.getCurrency("pay_amount"));
            } else if (oldData.getString("ready_to_pay_flag") == "N" && newData.getString("ready_to_pay_flag") == "N") {
                await this.subCurrency(existingRow.data.get("hold_total"), oldData.getCurrency("pay_amount"));
                await this.addCurrency(existingRow.data.get("hold_total"), newData.getCurrency("pay_amount"));
            } else if (oldData.getString("ready_to_pay_flag") == "Y" && newData.getString("ready_to_pay_flag") == "N") {
                await this.subCurrency(existingRow.data.get("process_total"), oldData.getCurrency("pay_amount"));
                await this.addCurrency(existingRow.data.get("hold_total"), newData.getCurrency("pay_amount"));
            } else if (oldData.getString("ready_to_pay_flag") == "N" && newData.getString("ready_to_pay_flag") == "Y") {
                await this.subCurrency(existingRow.data.get("hold_total"), oldData.getCurrency("pay_amount"));
                await this.addCurrency(existingRow.data.get("process_total"), newData.getCurrency("pay_amount"));
            }
            existingRow.data.set("freeze_by", newData.get("freeze_by"));
            if (oldData.get("payee_name") != newData.get("payee_name") && this.getGroupByFieldName() == "payee_id") {
                existingRow.data.set("name", newData.get("payee_name"));
            }
            if (oldData.get("factoring_company_name") != newData.get("factoring_company_name") && this.getGroupByFieldName() == "factoring_company_id") {
                existingRow.data.set("name", newData.get("factoring_company_name"));
            }
            this.tableGroupBy.redisplaySingleRow(existingRow.index, existingRow.data, false, false);
        } else {
            await this.handleDelete(oldData, existingRow);
            await this.handleAdd(newData);
        }
    }

    createNewGroupByRow(newData: ModelRow) {
        const newRow = new ModelRow("", false);
        newRow.set("group_by_value", newData.get(this.getGroupByFieldName()));
        newRow.set("name", this.getGroupByFieldName() === "payee_id" ? newData.get("payee_name") : this.getGroupByFieldName() === "factoring_company_id" ? newData.get("factoring_company_name") : null);
        newRow.set("count", 1);
        newRow.set("hold_total", newData.getString("ready_to_pay_flag") == "Y" ? this.createFilteredCurrency() : newData.getCurrency("pay_amount"));
        newRow.set("process_total", newData.getString("ready_to_pay_flag") == "Y" ? newData.getCurrency("pay_amount") : this.createFilteredCurrency());
        newRow.set("freeze_by", newData.get("freeze_by"));
        this.tableGroupBy.addRow(newRow, null, { display: true, addToData: true });
    }


    textboxGroupByOnChange(event: ChangeEvent) {
        this.groupByField();
    }

    textboxTotalOnPrintable(event: PrintableEvent) {
        const textbox = event.target as Textbox;
        textbox.captionProps = { align: HorizontalAlignment.RIGHT };
    }

    async setTabTitles(): Promise<void> {
        const tabMap = new Map<string, {
            count: number,
            total: Currency,
            getLayoutBaseFilter: (row: ModelRow) => boolean
        }>();
        this.tabsetOrderPayables.forEveryChildComponent(async (component) => {
            if (component instanceof Tab) {
                const tab = component as Tab;
                if (tab.id == "tabProcess" || tab.id == "tabPendingReview" || tab.id == "tabHold") {
                    const layout = tab.findComponentByType(Layout) as OrderPayablesTabLayout;
                    tabMap.set(tab.id, {
                        count: 0,
                        total: this.createFilteredCurrency(),
                        getLayoutBaseFilter: layout.getLayoutBaseFilter
                    });
                }
            }
        });
        const currencySettings = CurrencySettings.getSingleton();
        for (const row of this.mainDataSource.data) {
            for (const [key, value] of tabMap) {
                if (value.getLayoutBaseFilter(row) && this.profileFilter.dataPasses(row) && (this.tableGroupBy.selectedRows.length == 0 || this.tableGroupBy.selectedRows.find(selectedRow => selectedRow.data.get("group_by_value") == row.get(this.getGroupByFieldName())) != null)) {
                    if (!currencySettings.multiCurrencyEnabled() || row.getCurrency("pay_amount")?.getCurrencyCode() == this.profileFilter.getFilterCurrencyType()) {
                        await value.total.add(row.getCurrency("pay_amount"));
                        if (this.shouldDisplayRow(row)) {
                            value.count++;
                        }
                    }
                }
            }
        }
        tabMap.forEach((value, key) => {
            const tab = this.tabsetOrderPayables.findComponentById(key) as Tab;
            const count = value.count;
            if (tab.caption.includes("(")) {
                tab.caption = tab.caption.split("(")[0].trim();
            }
            tab.caption = `${tab.caption} (${count}) ${value.total}`;
        });
        this.tabsetOrderPayables.refreshUnderline();
    }

    createFilteredCurrency(): Currency {
        const currencySettings = CurrencySettings.getSingleton();
        const multicurrencyEnabled = currencySettings.multiCurrencyEnabled();

        if (!multicurrencyEnabled) return new Currency(0);

        return new Currency({ currency_code: this.profileFilter.getFilterCurrencyType(), amount: 0 });
    }

    async addCurrency(total: Currency, newCurrency: Currency) {
        if (total == null || newCurrency == null) {
            return;
        }

        const filterCurrency = this.profileFilter.getFilterCurrencyType();
        if (filterCurrency == newCurrency.getCurrencyCode() || filterCurrency == null) {
            await total.add(newCurrency);
        }
    }

    async subCurrency(total: Currency, newCurrency: Currency) {
        if (total == null || newCurrency == null) {
            return;
        }

        const filterCurrency = this.profileFilter.getFilterCurrencyType();
        if (filterCurrency == newCurrency.getCurrencyCode() || filterCurrency == null) {
            await total.subtract(newCurrency);
        }
    }

    public getProfileFilter(): OrderPayablesFilter {
        return this.profileFilter;
    }
}
